import React from 'react';
import { ConstraintScope } from '@finsera/finsera/dist/resource/optimizer';
import * as enums from '@local/finsera-core/src/enums';
import { useEffectWithoutFirst } from '@local/finsera-core/src/hooks';
import { _, mui } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

import ResourceAutocomplete from '../../../resource-autocomplete';
import { getConstraintType } from '../../optimizer-utils/constraint-type';
import Bounds from '../bounds';
import SharedConstraintFields from '../shared-fields';
import Target from '../target';

type SignalExposureConstraintProps = {
  id: string;
  params: types.optimizer.SignalExposureDraft;
  setParams: (_params: types.optimizer.SignalExposureDraft) => void;
  readOnly: boolean;
  validationDef: types.common.ValidationErrors;
  optimizerType: 'basket' | 'backtest';
  isLongOnly: boolean;
};

const SignalExposureConstraint = ({
  id,
  params,
  setParams,
  readOnly,
  optimizerType,
  validationDef,
  isLongOnly,
}: SignalExposureConstraintProps) => {
  const [showMore, setShowMore] = React.useState(false);
  const [constraint, setConstraint] = React.useState(params);
  const disableLower = constraint.scope === 'SHORT' || constraint.scope === 'LONG';

  React.useEffect(() => {
    if (optimizerType == 'basket' && params.benchmark_relative == null) {
      setConstraint({ ...constraint, benchmark_relative: true });
    }
  }, []);

  useEffectWithoutFirst(() => {
    setParams(constraint);
  }, [constraint]);

  const form = () => {
    if (getConstraintType(constraint) == 'BOUNDS') {
      if (showMore) {
        return (
          <Bounds
            id={id}
            params={constraint}
            setParams={setConstraint}
            readOnly={readOnly}
            validationDef={validationDef}
            disableLower={disableLower}
          >
            <SharedConstraintFields
              id={id}
              params={constraint}
              setParams={setConstraint}
              readOnly={readOnly}
              validationDef={validationDef}
            />
          </Bounds>
        );
      }

      return (
        <Bounds
          id={id}
          params={constraint}
          setParams={setConstraint}
          readOnly={readOnly}
          validationDef={validationDef}
          disableLower={disableLower}
        />
      );
    }

    if (showMore) {
      return (
        <Target params={constraint} setParams={setConstraint} readOnly={readOnly}>
          <SharedConstraintFields
            id={id}
            params={constraint}
            setParams={setConstraint}
            readOnly={readOnly}
            validationDef={validationDef}
          />
        </Target>
      );
    }

    return <Target params={constraint} setParams={setConstraint} readOnly={readOnly} />;
  };

  const benchmarkRelativeToggle = () => {
    return (
      <mui.core.FormControlLabel
        control={
          <mui.core.Switch
            size="small"
            checked={constraint.benchmark_relative}
            onChange={() =>
              setConstraint({
                ...constraint,
                benchmark_relative: !constraint.benchmark_relative,
              })
            }
            disabled={readOnly}
          />
        }
        label={optimizerType == 'backtest' ? 'Benchmark Relative' : 'Relative to Target Weights'}
      />
    );
  };

  const pipeline_ids = constraint.pipeline_ids || (constraint.pipeline_id != null ? [constraint.pipeline_id] : []);

  return (
    <>
      {/* {!readOnly && (
        <ui.Button
          sx={uiStyles.fullCardButton}
          color="primary"
          size="small"
          fullWidth
          onClick={() => setConstraint({ 
            type: constraint.type == 'BOUNDS' ? 'TARGET' : 'BOUNDS',
            target_penalty: constraint.type == 'BOUNDS' ? 1.0 : undefined, 
          })}
        >
          { constraint.type == 'BOUNDS' ? 'Change to target mode' : 'Change to bounds mode' }
        </ui.Button>
      )} */}

      <mui.core.Box p={3}>
        {!isLongOnly && (
          <mui.core.Grid container spacing={4}>
            <mui.core.Grid size={{ xs: 12, md: 6 }}>
              <mui.core.TextField
                select
                label="Scope"
                variant="outlined"
                size="small"
                sx={{ minWidth: 'fit-content' }}
                value={constraint.scope || 'ANY'}
                onChange={(e) => {
                  const newScope = e.target.value as ConstraintScope;
                  if (newScope == 'ANY') {
                    setConstraint({ ...constraint, scope: newScope });
                  } else {
                    setConstraint({ ...constraint, lower_bound: 0.0, scope: newScope });
                  }
                }}
                fullWidth
                disabled={readOnly}
                required
              >
                <mui.core.MenuItem value={'ANY'}>All Positions</mui.core.MenuItem>
                <mui.core.MenuItem value={'LONG'}>Long Only</mui.core.MenuItem>
                <mui.core.MenuItem value={'SHORT'}>Short Only</mui.core.MenuItem>
              </mui.core.TextField>
            </mui.core.Grid>
            <mui.core.Grid size={{ xs: 12, md: 6 }}>{benchmarkRelativeToggle()}</mui.core.Grid>
          </mui.core.Grid>
        )}
        {isLongOnly && benchmarkRelativeToggle()}
      </mui.core.Box>
      <mui.core.Box px={3}>
        <mui.core.Grid container spacing={4}>
          <mui.core.Grid size={{ xs: 12, md: 6 }}>
            <ResourceAutocomplete<types.signal.SignalExpanded>
              value={constraint.signal_id}
              setValue={(id) => setConstraint({ ...constraint, signal_id: id })}
              type="signal_based"
              disabled={readOnly}
              sortBy={['type', 'ws', (el: any[]) => el.length]}
              inputProps={{
                fullWidth: true,
                label: 'Signal / Alpha Model',
                variant: 'outlined',
                size: 'small',
                required: true,
                disabled: readOnly,
                error: validationDef?.fields?.includes(`signal_${id}`),
              }}
            />
          </mui.core.Grid>
          <mui.core.Grid size={{ xs: 12, md: 6 }}>
            <ResourceAutocomplete<types.pipeline.PipelineExpanded, true, false>
              value={_.isArray(pipeline_ids) ? pipeline_ids : [pipeline_ids]}
              // Setting pipeline_id as null for legacy issues
              setValue={(ids) => setConstraint({ ...constraint, pipeline_id: null, pipeline_ids: ids })}
              type={enums.RESOURCES_TYPES_ENUM.PIPELINE}
              disabled={readOnly}
              autocompleteProps={{
                size: 'small',
                multiple: true,
              }}
              inputProps={{
                fullWidth: true,
                label: 'Pipelines',
                variant: 'outlined',
                size: 'small',
              }}
            />
          </mui.core.Grid>
        </mui.core.Grid>
      </mui.core.Box>

      <mui.core.Box p={3}>
        {form()}
        <mui.core.Button
          onClick={() => {
            setShowMore(!showMore);
          }}
          color="primary"
          sx={{ mt: 3 }}
          startIcon={showMore ? <mui.icons.Remove /> : <mui.icons.Add />}
        >
          {showMore ? <>Show Less</> : <>Show More</>}
        </mui.core.Button>
      </mui.core.Box>
    </>
  );
};

export default SignalExposureConstraint;
